/**
 * Category Tabs — Sticky horizontal tab strip
 * - Highlights the active category
 * - Auto-scrolls to keep active tab centred in view
 * - Driven externally by scroll-spy; click triggers scrollToCategory()
 * - Hides tabs for categories with no visible items after dietary filtering
 */

import { getCurrentLanguage } from '../../../i18n';
import { scrollToCategory, centerActiveTab } from '../scroll-spy';
import type { MenuCategory } from '../types';
import type { ActiveFilters } from './diet-filter';

export function renderCategoryTabs(
  categories: MenuCategory[],
  activeCatId: string,
  filters: ActiveFilters,
): string {
  const lang = getCurrentLanguage();

  // Only show tabs for visible categories (have at least one matching item)
  const visibleCats = categories.filter((cat) =>
    cat.active && cat.items.some((item) => itemMatchesFilters(item.tags, filters) && item.available)
  );

  if (visibleCats.length === 0) return '';

  const tabs = visibleCats.map((cat) => {
    const name = (cat.name as Record<string, string>)[lang] || cat.name.en;
    const isActive = cat.id === activeCatId;

    return `
      <button
        id="cat-tab-${cat.id}"
        class="cat-tab${isActive ? ' cat-tab--active' : ''}"
        onclick="window.onCatTabClick?.('${cat.id}')"
        aria-current="${isActive ? 'true' : 'false'}"
        style="
          flex:0 0 auto;padding:10px 16px;border:none;border-bottom:2px solid ${isActive ? '#00B900' : 'transparent'};
          background:none;color:${isActive ? '#00B900' : '#666'};
          font-size:13px;font-weight:${isActive ? '700' : '500'};
          cursor:pointer;white-space:nowrap;transition:all 0.15s ease;
          touch-action:manipulation;
        "
      >${name}</button>
    `;
  }).join('');

  return `
    <div id="cat-tabs-strip" role="tablist" style="
      display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;
      background:#fff;border-bottom:1px solid #e0e0e0;
      scrollbar-width:none;-ms-overflow-style:none;
    ">${tabs}</div>
  `;
}

/** Update active tab style in-place (called by scroll-spy callback) */
export function setActiveTabUI(catId: string): void {
  document.querySelectorAll<HTMLButtonElement>('.cat-tab').forEach((btn) => {
    const isActive = btn.id === `cat-tab-${catId}`;
    btn.classList.toggle('cat-tab--active', isActive);
    btn.setAttribute('aria-current', isActive ? 'true' : 'false');
    btn.style.borderBottomColor = isActive ? '#00B900' : 'transparent';
    btn.style.color = isActive ? '#00B900' : '#666';
    btn.style.fontWeight = isActive ? '700' : '500';
  });
  centerActiveTab(catId);
}

/** Called when user clicks a tab */
export function handleCatTabClick(catId: string): void {
  setActiveTabUI(catId);
  scrollToCategory(catId);
}

/** Helper: does an item match the active dietary filters? */
export function itemMatchesFilters(tags: string[], filters: ActiveFilters): boolean {
  if (filters.size === 0) return true;
  return [...filters].every((f) => tags.includes(f));
}
